import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import Label from 'LuxKit/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import ViewBundleOffer from 'components/OfferList/OfferListTiles/SearchOfferTiles/ViewBundleOffer'
import HotelSearchTileAlternativeDatesBanner from 'components/SearchKit/SearchTiles/accommodations/HotelSearchTileAlternativeDatesBanner'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import useOffer from 'hooks/Offers/useOffer'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import useAccommodationMapSearchPath from 'hooks/Search/useAccommodationMapSearchPath'
import useMapSearchOfferUrl from 'hooks/Search/useMapSearchOfferUrl'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import { nonNullable } from 'lib/array/arrayUtils'
import { getPackageUniqueKey, isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import { reportLog } from 'services/userMonitoring'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchTile from '../SearchTile'
import SearchTileHighlightBanner from '../shared/SearchTileHighlightBanner'
import SearchTileRowOverlineLocation from '../shared/SearchTileRowOverlineLocation'
import ExperimentalHotelSearchTileFeatures from './ExperimentalHotelSearchTileFeatures'
import HotelSearchTileAction from './HotelSearchTileAction'
import HotelSearchTileEarnablePoints from './HotelSearchTileEarnablePoints'
import HotelSearchTileFeatures from './HotelSearchTileFeatures'
import HotelSearchTileHighlightBanner from './HotelSearchTileHighlightBanner'
import HotelSearchTileLabels from './HotelSearchTileLabels'
import useMetaSearchDistance from './useMetaSearchDistance'
import HotelSearchTilePriceStack from './HotelSearchTilePriceStack'
import HotelSearchTileUnavailabilityBanner from './HotelSearchTileUnavailabilityBanner'
import HotelSearchTileCharterFareClassBanner from './HotelSearchTileCharterFareClassBanner'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SearchTileComparisonCheckbox from 'components/SearchKit/SearchTiles/shared/SearchTileComparisonCheckbox'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { diffInDays } from 'lib/datetime/dateUtils'
import { generalLog } from 'marketing/api'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import useSearchPropertyRateLoyaltyPackageUpgrade from 'loyalty/hooks/useSearchPropertyRateLoyaltyPackageUpgrade'
import LoyaltyStayUpgradeBenefitMessageBanner from 'loyalty/components/LoyaltyStayUpgradeBenefitMessageBanner'
import useOfferRating from 'hooks/Offers/useOfferRating'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { isMiguelMaestreHostedOffer } from 'lib/offer/offerSpecificChangesUtils'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { CHARTER_PAGE_SLUG, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import { getIsSpecificCampaignPage } from 'selectors/routerSelectors'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import Group from 'components/utils/Group'
import useSearchTargetName from 'hooks/Search/useSearchTargetName'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'
import { getAccommodationSearchTileView } from './accommodationSearchTileView'
import { useMaldivesDirectBundleContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectBundleContext'
import { getCancellationCheckInDate } from './hotelSearchTileCancellation'
import { isDisplayablePrice } from 'lib/offer/pricingVisibility'
import { getAccommodationSearchProductType } from 'lib/search/accommodationSearchPresentation'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  filters?: App.OfferListFilters
  showPerNightPricing?: boolean
  storeSearchTile?: boolean
  showDescription?: boolean
  position?: number
  eagerLoadFirstImage?: boolean
  /**
   * @default default
   */
  density?: SearchTileDensity
  /**
   * @default default
   */
  treatment?: SearchTileTreatment
}

function HotelSearchTile({
  offer,
  filters,
  showPerNightPricing = false,
  storeSearchTile = false,
  showDescription = false,
  position,
  eagerLoadFirstImage,
  density = 'default',
  treatment = 'default',
}: Props) {
  const tileView = getAccommodationSearchTileView(density)
  const intl = useIntl()
  const { maldivesBundleLabel } = useCharterFlightLabels()
  const utmSource = useAppSelector(state => state.utm.source)
  const noGoogleAffiliations = utmSource !== 'google'
  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const maldivesDirectBundleContext = useMaldivesDirectBundleContext()
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters?.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters?.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const isSpecificSearch = !!(checkIn && checkOut && filters?.rooms)
  const queryParams = useAppSelector((state) => state.router.location.query)
  const fullPath = useAppSelector((state) => state.router.location.pathname + state.router.location.search)

  const [, fetchingOffer] = useOffer<App.Offer>(offer.id, {
    requireSummaryOnly: !isSpecificSearch,
  })

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, filters?.rooms), [checkIn, checkOut, filters?.rooms])

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const cartItemPackageUniqueKey = filters?.oneClickBookingRestoration?.packageUniqueKey
  const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration

  const hasFiltersSet = !!(checkIn && checkOut && filters?.rooms)

  const bestPackage = useMemo<App.HotelPackage | undefined>(() => {
    // The one-click booking restoration tile must show the exact package selected in
    // checkout, not the normal search/best-price package for the offer.
    if (cartItemPackageUniqueKey) {
      const cartPackage = offer.packages.find(pkg => pkg.uniqueKey === cartItemPackageUniqueKey)
      if (cartPackage) return cartPackage
    }

    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage && fullPath.includes('metasearch')) {
        const priceTotal = queryParams.priceTotal || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceTotal) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          reportLog('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      // use search pricing package id by default

      if (!pkgId || !duration || !bestMaybeAvailableRate?.rate?.roomRateId) {
        return offer.lowestPricePackage
      }

      const pkg = offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, bestMaybeAvailableRate.rate.roomRateId))
      return pkg ?? offer.lowestPricePackage
    }
  }, [shouldUseRealSearchPrices, cartItemPackageUniqueKey, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const correctBestPackage = useMemo<App.HotelPackage | undefined>(() => {
    // Keep the non-Google/experimental feature list aligned with the restored
    // checkout package, so inclusions don't describe a different best-price package.
    if (cartItemPackageUniqueKey) {
      const cartPackage = offer.packages.find(pkg => pkg.uniqueKey === cartItemPackageUniqueKey)
      if (cartPackage) return cartPackage
    }

    if (filters?.roomRateId && filters?.roomTypeId) {
      const nightDuration = checkIn && checkOut ? diffInDays(new Date(checkIn), new Date(checkOut)) : 1
      const highlightedPackage = offer.packages.find(pkg =>
        pkg.roomRate.id === filters?.roomRateId &&
        pkg.roomType.id === filters?.roomTypeId &&
        pkg.duration === nightDuration,
      )
      if (highlightedPackage) {
        const priceShown = queryParams.priceShown || 0
        generalLog({
          status: 'info',
          message: 'GoogleHotelSearchPage: highlightedPackage',
          payload: { ...queryParams, fullPath, priceReceived: highlightedPackage.price, priceDifference: Number(priceShown) - highlightedPackage.price },
        })
        return highlightedPackage
      }
    }
    if (!shouldUseRealSearchPrices) {
      if (!hasFiltersSet || bestPriceError) {
        return offer.lowestPricePackage
      }
      if (bestMaybeAvailableRate?.rate && !fetchingOffer) {
        const bestPricePkg = offer.packages.find(pkg => pkg.uniqueKey === bestMaybeAvailableRate.rate.packageUniqueKey)
        if (!bestPricePkg) {
          reportLog('Missing best price package from offer', {
            offerId: offer.id,
            packages: offer.packages.map(pkg => pkg.uniqueKey),
            searchKey,
            bestPriceRate: bestMaybeAvailableRate.rate,
          })
        }
        return bestPricePkg ?? offer.lowestPricePackage
      }
    } else {
      // use search pricing package id by default
      const pkgId = metaData?.pricing?.lowestPricePackageId ?? offer.lowestPricePackage?.id
      const roomRateId = metaData?.pricing?.lowestPriceRoomRateId ?? bestMaybeAvailableRate?.rate?.roomRateId

      if (!pkgId || !duration || !roomRateId) {
        return
      }

      return offer.packages.find(pkg => pkg.uniqueKey === getPackageUniqueKey(pkgId, duration, roomRateId))
    }
  }, [shouldUseRealSearchPrices, cartItemPackageUniqueKey, filters, checkIn, checkOut, offer, hasFiltersSet, bestPriceError, bestMaybeAvailableRate, fetchingOffer, searchKey, metaData, duration, queryParams, fullPath])

  const cancellationPolicyType = bestPackage?.roomRate?.cancellationPolicy?.type

  const productType = getAccommodationSearchProductType(
    offer.productType,
    isLPCTacticalEnabled() && !!offer.hasTactical,
  )

  const locations = useMemo<Array<string>>(() => nonNullable([offer.locationHeading, offer.locationSubheading]), [offer])
  const mapSearchPath = useAccommodationMapSearchPath()
  const mapUrl = useMapSearchOfferUrl(storeSearchTile, offer, filters, mapSearchPath)
  const distanceFromSearchTarget = metaData?.distance
  const searchTargetName = useSearchTargetName(filters)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const showProductLabel = isSpoofed || productType !== OFFER_TYPE_ALWAYS_ON

  const isSoldOutFromSearch = shouldUseRealSearchPrices
    ? !metaData?.available
    : (hasFiltersSet && bestMaybeAvailableRate !== undefined && !bestMaybeAvailableRate.available)

  const isSoldOut = isSoldOutFromSearch

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  useEffect(() => {
    if (isSoldOut) {
      dispatchOfferListEvent({
        type: OfferListEvents.pricingCalculated,
        offerId: offer.id,
        availability: false,
        price: undefined,
        roomRateId: undefined,
        memberPrice: undefined,
        duration: undefined,
        extendedAvailability: metaData?.extendedAvailability,
      })
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldOut])

  const showSpoofHighlight = useMemo<boolean>(() => {
    return isSpoofed && !scheduleIsCurrent(offer.visibilitySchedule)
  }, [isSpoofed, offer.visibilitySchedule])

  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights

  const showHighlight = useMemo(() => {
    if (showSpoofHighlight) return true
    if (showDescription) return false

    if (!filters?.propertyId || filters?.searchNearby) return false
    return offer.property?.id === filters.propertyId
  }, [filters, offer, showSpoofHighlight, showDescription])

  // On metasearch, the bedbank property id arrives via `propertyId` for the
  // slot 1 tile and via `showDistanceWithMetaOfferId` for the YMAL/distance
  // tiles. Either form keys into `alternativeOffersForProperty`.
  const metaSearchSourcePropertyId = filters?.propertyId ?? filters?.showDistanceWithMetaOfferId
  // Selector returns a primitive boolean rather than the `leOfferDetails`
  // array — `useAppSelector` defaults to reference equality, so returning
  // `.map(...)` or the array slice would invalidate every tile on the list
  // on every Redux dispatch.
  const isMetaSearchSamePropertyMatch = useAppSelector((state) => {
    if (!fullPath.includes('metasearch')) return false
    if (filters?.searchNearby) return false
    if (!metaSearchSourcePropertyId) return false
    const details = state.offer.alternativeOffersForProperty[metaSearchSourcePropertyId]?.leOfferDetails
    return details?.some((d) => d.offerId === offer.id) ?? false
  })

  const showSpecificRoomRateAndRoomType = filters?.roomRateId && filters?.roomTypeId
  // Restoration tiles prefer the exact live cart package rate when available,
  // so earnable points and price-dependent badges follow the restored package.
  const checkoutCartAvailableRateKey = useMemo(() => {
    if (!checkIn || !checkOut || !filters?.rooms || !cartItemPackageUniqueKey) return undefined
    return buildAvailableRateKey(checkIn, checkOut, filters.rooms)
  }, [cartItemPackageUniqueKey, checkIn, checkOut, filters?.rooms])
  const checkoutCartAvailableRate = useAppSelector<App.OfferAvailableRate | undefined>((state) => {
    if (!checkoutCartAvailableRateKey || !cartItemPackageUniqueKey) return undefined

    return state.offer.offerAvailableRatesByOccupancy[offer.id]?.[checkoutCartAvailableRateKey]?.rates
      .find(rate => rate.packageUniqueKey === cartItemPackageUniqueKey)
  })
  const bestAvailableRate = checkoutCartAvailableRate ?? (bestMaybeAvailableRate?.available && !showSpecificRoomRateAndRoomType ? bestMaybeAvailableRate.rate : undefined)
  const { defaultPricing: tileDisplayPricing } = useSearchPrices({
    offer,
    pkg: bestPackage,
    rate: bestAvailableRate,
    duration: duration ?? 1,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
  })
  const hidePrice = useHideAnytimePrices(offer.type, metaData?.hidePricing, productType, filters)
  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const currency = useContext(CurrencyContext)
  const rating = useOfferRating(offer, metaData, fullPath.includes('metasearch'))
  const userHasUpgradesBenefitAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const isUpgradeAvailableForSelectedDates = isAnytimeSearch ? true : (metaData?.packagesWithAvailableUpgrade.length ?? 0) > 0

  const hasHotelUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades
  const { available: hasPackageRateUpgrade } = useSearchPropertyRateLoyaltyPackageUpgrade(offer, {
    checkIn,
    checkOut,
    rooms: filters?.rooms,
    enabled: tileView.showUpgradeBanner,
  })

  const showTasteOfGoldBenefit =
    isTasteOfGoldActive &&
    isTasteOfGoldEligibleOffer(offer, currency, offer.property.geoData.countryCode?.toLowerCase() !== 'au', tileDisplayPricing.price)

  const isMaldivesCharter = useAppSelector((state: App.State) => getIsSpecificCampaignPage(state, CHARTER_PAGE_SLUG))

  // Check if this is a charter bundle offer by checking campaignTags
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])
  const cancellationCheckInDate = getCancellationCheckInDate({
    checkInDate: filters?.checkIn,
    hasUserSelectedDates: maldivesDirectBundleContext.hasUserSelectedDates,
    isCharterBundleOffer,
    isMaldivesDirectBundle: maldivesDirectBundleContext.charterBundle,
  })

  const hotelFlightBundleLabel = intl.formatMessage({
    id: 'PjmdhR5JAQXM7v4WOevW',
    defaultMessage: 'Hotel & Flight bundle',
    description: 'Label for charter bundle offers combining hotel and flight',
  })
  const isFlashHotelProductType = productType === OFFER_TYPE_HOTEL

  const productTypeLabelNode = useMemo<React.ReactNode>(() => {
    if (isCharterBundleOffer) {
      return <NeutralLabel>{hotelFlightBundleLabel}</NeutralLabel>
    }
    if (isMaldivesCharter) {
      return null
    }
    if (!isFlashHotelProductType && !showProductLabel) {
      return null
    }

    const label = <ProductTypeLabel productType={productType} />

    if (isFlashHotelProductType) {
      return (
        <LtleTooltipWrapper autoOpenOnce>
          {label}
        </LtleTooltipWrapper>
      )
    }

    return label
  }, [isCharterBundleOffer, isMaldivesCharter, isFlashHotelProductType, showProductLabel, productType, hotelFlightBundleLabel])

  const handleSelectDatesClick = useCallback(() => {
    if (onAnyHotelSelectDatesPrompt) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  const metaSearchDistanceToMainOffer = useMetaSearchDistance({ offer, filters })
  const showPricingBlock = !isSoldOut && (hidePrice || isDisplayablePrice(tileDisplayPricing.price))

  const comparisonPriceTotals = useMemo(() => {
    if (!bestPackage) return undefined
    return { price: bestPackage.price, memberPrice: bestPackage.memberPrice, value: bestPackage.value }
  }, [bestPackage])

  const shouldAppendFlights = isCharterBundleOffer && !offer.property.name.toLocaleLowerCase().includes(' & flights')

  let offerTitle: string
  if (!shouldAppendFlights) {
    offerTitle = offer.property.name
  } else if (isMiguelMaestreHostedOffer(offer.id)) {
    offerTitle = intl.formatMessage(
      {
        id: 'hfpoYvBsuWKpllDOxP9q',
        defaultMessage: '{propertyName} & Flights with Miguel Maestre',
        description: 'Hotel search tile title for the charter bundle departure hosted by Miguel Maestre, appended after the flights bundle suffix',
      },
      { propertyName: offer.property.name },
    )
  } else {
    offerTitle = intl.formatMessage(
      {
        id: 'jlWVmAHHGkjvRL3MaCvy',
        defaultMessage: '{propertyName} & Flights',
        description: 'Hotel search tile title appended with flights bundle suffix for charter offers',
      },
      { propertyName: offer.property.name },
    )
  }
  const hotelSearchTileFeatures = !noGoogleAffiliations ?
      <HotelSearchTileFeatures
        offer={offer}
        offerMetaData={metaData}
        filters={filters}
        bestPackage={bestPackage}
        doNotOpenInclusionsModal={storeSearchTile}
        maxTotalInclusions={tileView.hotelMaxTotalInclusions}
        hideFeatureTitle={!tileView.showFeatureTitle}
      /> :
      <ExperimentalHotelSearchTileFeatures
        offer={offer}
        offerMetaData={metaData}
        filters={filters}
        bestPackage={correctBestPackage}
        maxInclusionsToShow={tileView.hotelMaxTotalInclusions}
        hideSecondaryLuxPlusPromos={!tileView.showSecondaryLuxPlusPromos}
      />

  return <SearchTile
    offer={offer}
    density={density}
    treatment={treatment}
    eagerLoadFirstImage={eagerLoadFirstImage}
    state={showHighlight ? 'highlighted' : 'default'}
    action={!isSoldOut && <HotelSearchTileAction offer={offer} offerMetaData={metaData} onSelectDatesClick={onAnyHotelSelectDatesPrompt ? handleSelectDatesClick : undefined} filters={filters} productType={productType} />}
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
          position={position}
        />
      )}
      {isMetaSearchSamePropertyMatch && <SearchTileHighlightBanner
        description={intl.formatMessage({
          id: 'a8iSwYFsIlZmMvff2y0H',
          defaultMessage: 'Same property, better deal',
          description: 'Banner shown above the flash offer tile (slot 2) on the metasearch page when we surface a flash deal for the same property the user landed on from Google Hotel Ads',
        })}
      />}
      {showHighlight && !isMetaSearchSamePropertyMatch && <HotelSearchTileHighlightBanner offer={offer} offerMetaData={metaData} filters={filters} />}
      <HotelSearchTileCharterFareClassBanner offerId={offer.id} />
      {offerSiteTakeover?.searchPage?.tileBanner}
    </>}
    bookmarkAction={storeSearchTile ? undefined : <BookmarkButton offer={offer} withLabel="always" />}
    cancellationPolicyLabel={tileView.showCancellationPolicy && (!noGoogleAffiliations || isFlashHotelOffer(offer)) && !!cancellationPolicyType && <TileCancellationPolicy
      cancellationPolicyType={cancellationPolicyType}
      checkInDate={cancellationCheckInDate}
      timezoneOffset={offer.property.timezoneOffset}
      offerType={offer.type}
    />}
    bottomExtension={<>
      {config.ENABLE_BUNDLE_AND_SAVE && metaData?.bundleOfferId && <ViewBundleOffer
        offer={offer}
        filters={filters}
        offerMetaData={metaData}
        offerLinkIncludesFilters
      />}
      {metaData?.extendedAvailability && <HotelSearchTileAlternativeDatesBanner
        checkIn={metaData.extendedAvailability.checkIn}
        checkOut={metaData.extendedAvailability.checkOut}
      />}
      {isSoldOut && !metaData?.extendedAvailability &&
        <HotelSearchTileUnavailabilityBanner
          offer={offer}
          unavailableReason={metaData?.unavailableReason}
          filters={filters}
        />}
    </>}
    topExtension={
      (hasHotelUpgradablePackages || hasPackageRateUpgrade) && userHasUpgradesBenefitAvailable && tileView.showUpgradeBanner && (
        <>
          {hasHotelUpgradablePackages && <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} upgradeNotAvailableForSelectedDates={!isUpgradeAvailableForSelectedDates} />}
          {!hasHotelUpgradablePackages && hasPackageRateUpgrade && <LoyaltyStayUpgradeBenefitMessageBanner offer={offer} showPackageUpgrades showRoomUpgrades={false} />}
        </>
      )
    }
    features={<>
      {hotelSearchTileFeatures}
    </>}
    images={offer.images}
    labels={
      <HotelSearchTileLabels
        offer={offer}
        cancellationPolicyType={cancellationPolicyType}
        urgencyTagVariant={tileView.urgencyTagVariant}
        filters={filters}
        bestPackage={bestPackage}
        showUrgencyTags={!isSoldOut}
        timezoneOffset={offer.property.timezoneOffset}
      />
    }
    overline={tileView.showLocationOverline && <SearchTileRowOverlineLocation
      locations={locations}
      mapUrl={isCharterBundleOffer || isMaldivesCharter ? undefined : mapUrl}
      distance={distanceFromSearchTarget}
      searchTargetLabel={searchTargetName}
      nearbyPois={metaData?.nearbyPois}
      propertyId={offer.property?.id}
      showDistanceLabels={tileView.showLocationDistanceLabels}
    />}
    pricePoint={showPricingBlock && <HotelSearchTilePriceStack
      offer={offer}
      filters={filters}
      bestPackage={bestPackage}
      duration={duration ?? 1}
      bestRate={bestAvailableRate}
      hidePrice={hidePrice}
      showPerNightPricing={showPerNightPricing}
      hideIncludesTaxesAndFees={storeSearchTile || tileView.hideIncludesTaxesAndFees}
      showCompactPricing={tileView.useCompactPricing}
    />}
    productLabel={<LabelGroup>
      {productTypeLabelNode}
      <OfferPartnershipLabel offer={offer} />
      {isMaldivesCharter && !isCharterBundleOffer && <Label kind="primary" variant="maldives-charter">{maldivesBundleLabel}</Label>}
      {filters?.showDistanceWithMetaOfferId && metaSearchDistanceToMainOffer && <PromotionLabel>{metaSearchDistanceToMainOffer}</PromotionLabel>}
    </LabelGroup>}
    rating={rating}
    title={offerTitle}
    meta={!storeSearchTile && tileView.showMeta && <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      <HotelSearchTileEarnablePoints
        offer={offer}
        bestPackage={bestPackage}
        bestAvailableRate={bestAvailableRate}
        filters={filters}
        metaData={metaData}
      />
      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
    </Group>}
    comparisonCheckbox={<SearchTileComparisonCheckbox
      offerType={offer.type}
      offerId={offer.id}
      name={offer.property.name}
      filters={filters}
      priceTotals={comparisonPriceTotals}
    />}
  />
}

export default HotelSearchTile
